import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useGetClaimsForCurrentUser } from '../api/user/user';
import { SignalREvent } from '../data-types/SignalREvent';
import { useIndexedDB } from '../hooks/useIndexedDB';
import { EventKeys } from '../utils/EventGrid.constants';
import { DB_VERSION } from '../utils/IndexedDBSchemaRegistry';
import { DB_WHITEPAPERREPORT_NAME, getWhitePaperCacheStoreName } from '../utils/WhitePaperIndexedDBUtils';
import { useCurrentEnterprise } from './useCurrentEnterprise';
import { useNotification } from './useNotification';

export function GlobalEventHandler(props: { children?: React.ReactNode }) {
    const { connection, addEventHandler } = useNotification();
    const { fetchEnterprise } = useCurrentEnterprise();
    const queryClient = useQueryClient();
    const { removePreviousVersions } = useIndexedDB(window.indexedDB, DB_VERSION);
    const { data: claims } = useGetClaimsForCurrentUser();

    const onEnterpriseChangeHandler = () => {
        queryClient.invalidateQueries(); // Invalidate all queries to refresh data
        fetchEnterprise();
    };

    // This is used to clear the cache when the tax return amounts are changed
    const onTaxReturnAmountChanged = async (eventData: SignalREvent<{ returnKey: number }>) => {
        if (eventData?.source === 'amountChange' && eventData?.notificationData?.returnKey) {
            const returnKey = eventData.notificationData.returnKey;

            const whitePaperStoreName = getWhitePaperCacheStoreName(claims);

            await removePreviousVersions(DB_WHITEPAPERREPORT_NAME, whitePaperStoreName!, '', {
                indexName: 'returnKey',
                query: returnKey.toString(),
            });
        }
    };

    useEffect(() => {
        if (connection) {
            connection.on(EventKeys.EnterpriseChange, onEnterpriseChangeHandler);
            const cleanupAmountsHandler = addEventHandler(EventKeys.AmountsUpdated, onTaxReturnAmountChanged, 1);

            return () => {
                connection.off(EventKeys.EnterpriseChange, onEnterpriseChangeHandler);
                cleanupAmountsHandler();
            };
        }
    }, [connection, onEnterpriseChangeHandler, addEventHandler]);

    return <>{props.children}</>;
}

export default GlobalEventHandler;
